import NavigationBar from "../comps/NavigationBar/NavigationBar";
import Footer from "../comps/Footer/Footer";
import ContentCard from "../comps/ContentCard/ContentCard";
import Sidebar from "../comps/Sidebar/Sidebar";

export default function Design() {
  const menu = [
    {
      name: "How to Find eCISs?",
      id: "Design-0",
    },
    {
      name: "Selecting the Appropriate Search Space",
      id: "Design-1",
    },
    {
      name: "Validation",
      id: "Design-2",
    },
    {
      name: "Exploring the Programmability of eCISs",
      id: "Design-3",
    },
  ];

  return (
    <div className="general-background">
      <NavigationBar />
      <Sidebar menu={menu} />
      <div className="main-container">
        <p className="title">Design:</p>
        <p className="subtitle" key={"Design-0"} id="Design-0">
          • How to Find eCISs?
        </p>
        <ContentCard
          content={`<p>
          Our mining strategy involves initially identifying the key homologous
          proteins that constitute the eCISs, followed by the recognition of the
          entire system.
          </p>
          <br />
          <p>
          After conducting literature research[1] and preliminary experiments (more
          on <a href="https://2023.igem.wiki/sysu-software/model">Model</a>), we have decided to begin by mining the afp1/5 in the input
          genome data. We will then select 40 proteins upstream and downstream of
          afp1/5 and predict the presence of afp11 and afp13. If all three proteins
          are present simultaneously, it will be considered a candidate eCIS.
          </p>
          <img
          src='https://static.igem.wiki/teams/4826/wiki/designfig1.png'
          alt='Figure 1. A schematic representation of BioRocket's strategy for mining eCIS gene clusters'
          class='content-figure'
          />
          <p class='content-legend'>
          Figure 1. A schematic representation of BioRocket's strategy for mining eCIS gene clusters
          </p>`}
        />
        <p className="subtitle" key={"Design-1"} id="Design-1">
          • Selecting the Appropriate Search Space:
        </p>
        <ContentCard
          content="<p>
            Our approach for determining the search space is to minimize unproductive
            searches while maximizing coverage of species diversity and protein space.
            </p>
            <br />
            <p>
            Therefore, we have opted to download data from the Genome Taxonomy
            Database (GTDB)[2]. This resource offers a phylogenetically coherent
            taxonomy for bacteria and archaea, encompassing isolate genomes as well as
            tens of thousands of metagenome-assembled genomes derived from various
            environmental and clinical samples.
            </p>
            <br />
            <p>
              The dataset for prediction consisted of approximately 200 million proteins
              from 80,789 bacterial species and 4,416 archaeal species.
            </p>"
        />
        <p className="subtitle" key={"Design-2"} id="Design-2">
          • Validation:
        </p>
        <ContentCard
          content={` <p>
          Validation is a crucial step in the DBTL (Design-Build-Test-Learn) cycle
          guided by artificial intelligence. High-throughput validation methods
          ensure the continuous generation of high-quality data, allowing algorithms
          to fully harness their predictive capabilities.
          </p>
          <br />
          <p>
            We primarily validate the reliability of our prediction algorithms through
            three avenues:
          </p>
          <br />
          <b>1.Validation Set Testing:</b>
          <p>
            By using validation datasets to assess and enhance the model's predictive
            capabilities.
          </p>
          <br />
          <b>2.Structural Prediction and Comparison:</b>
          <p>
            The ultimate determination of protein function relies on three-dimensional
            (3D) protein folds. By incorporating protein structure prediction and
            alignment methods, we integrated structural information into our
            large-scale mining approach.
          </p>
          <br />
          <p>
            We employed ESMfold[3] for high-throughput protein structure prediction
            and compared the structures of characteristic proteins(afp1/5) within
            candidate eCISs with data obtained from electron microscopy to ensure the
            reliability of the predicted data. The structure alignment was performed
            based on TM-score method[4].
          </p>
          <img
            src='https://static.igem.wiki/teams/4826/wiki/design-figure2.png'
            alt='Figure 2. The mining of eCISs based on structural information'
            class='content-figure'
          />
          <p class='content-legend'>
          Figure 2. The mining of eCISs based on structural information
          </p>
          <br />
          <b>3.Wet Lab Experiments:</b>
          <p>
          We conducted wet lab experiments to verify the reliability of the
          prediction results. For wet lab validation, the most straightforward
          approach is to directly express the predicted eCIS protein complexes.
          However, due to the typically long lengths of eCIS genes (around 20kb),
          their expression can be challenging and time-consuming. Research suggests
          that prokaryotes often use eCISs to deliver toxins, so toxin genes
          are commonly found in the vicinity of eCIS gene clusters[5]. Based on
          this, we opt to validate the presence of functional toxin genes near the
          predicted eCIS gene clusters(more on <a href="https://2023.igem.wiki/sysu-software/proof-of-concept">Proof of concept</a>). This indirect
          validation approach offers the advantage of high-throughput screening. For
          eCISs with higher reliability, we plan to collaborate with large
          DNA synthesis companies to synthesize and express them for functional
          validation in the future(more on <a href="https://2023.igem.wiki/sysu-software/entrepreneurship">Entrepreneurship</a>).
          </p>
          <img
            src='https://static.igem.wiki/teams/4826/wiki/design-figure3.png'
            alt='Figure 3. The three validation methods involved in BioRocket'
            class='content-figure'
          />
          <p class='content-legend'>
          Figure 3. The three validation methods involved in BioRocket
          </p>`}
        />
        <p className="subtitle" key={"Design-3"} id="Design-3">
          • Exploring the Programmability of eCISs:
        </p>
        <ContentCard
          content={`<p>
            We believe that the true superiority of eCISs as a therapeutic protein
            delivery system lies in its programmability.
          </p>
          <br />
          <p>
            For instance, certain studies have indicated that it's possible to
            artificially regulate the targeting of eCISs through synthetic biology
            approaches. However, due to the limited availability of known eCISs
            , achieving a more comprehensive analysis has remained unattainable.
            Now, our high-throughput mining approach has opened up the
            possibility of fully uncovering the programmability of eCISs.
          </p>
          <br />
          <p>
            By looking at reprogrammability of the natural form （eg:CRISPR system[6],
            Diversity-generating retroelement[7]）, one of the key features is that
            they have a hypervariable region. In the tail region of eCISs, we have
            identified a hypervariable segment. Through structural predictions, we
            believe alterations in this region hold the potential to regulate eCIS
            targeting specificity（more on <a href="https://2023.igem.wiki/sysu-software/engineering">Engineering</a>).
          </p>
        <img
            src='https://static.igem.wiki/teams/4826/wiki/design-figure4.jpg'
            alt='Figure 4. By comparing eCIS from different species sources, we have identified 
            the hypervariable region at the tail end of the afp13 homologous protein, speculating 
            that it is associated with the target specificity of eCISs.'
            class='content-figure'
          />
          <p class='content-legend'>
          Figure 4. By comparing eCISs from different species sources, we have identified the 
          hypervariable region at the tail end of the afp13 homologous protein, speculating that 
          it is associated with the target specificity of eCISs.
          </p>`}
        />
        <p className="reference-title">• Reference:</p>
        <ContentCard
          content="
          <p class='reference'>
          [1] Chen L, Song N, Liu B, et al. Genome-wide Identification and
          Characterization of a Superfamily of Bacterial Extracellular Contractile
          Injection Systems. Cell Rep. 2019;29(2):511-521.e2.
          doi:10.1016/j.celrep.2019.08.096
          </p>
          <p class='reference'>
            [2] Parks DH, Chuvochina M, Rinke C, Mussig AJ, Chaumeil PA, Hugenholtz P.
            GTDB: an ongoing census of bacterial and archaeal diversity through a
            phylogenetically consistent, rank normalized and complete genome-based
            taxonomy. Nucleic Acids Res. 2022;50(D1):D785-D794.
            doi:10.1093/nar/gkab776
          </p>
          <p class='reference'>
            [3] Lin Z, et al. Evolutionary-scale prediction of atomic-level protein
            structure with a language model. Science. 2023;379:1123-1130.
            doi:10.1126/science.ade2574.
          </p>
          <p class='reference'>
            [4] Zhang C, Shine M, Pyle AM, et al. US-align: universal structure
            alignments of proteins, nucleic acids, and macromolecular complexes. Nat
            Methods. 2022;19:1109-1115. doi:10.1038/s41592-022-01585-1.
          </p>
          <p class='reference'>
            [5] Geller AM, Pollin I, Zlotkin D, et al. The extracellular contractile
            injection system is enriched in environmental microbes and associates with
            numerous toxins. Nat Commun. 2021;12(1):3743. Published 2021 Jun 18.
            doi:10.1038/s41467-021-23777-7
          </p>
          <p class='reference'>
            [6] Altae-Tran H, Gao L, Strecker J, Macrae RK, Zhang F. Computational
            Identification of Repeat-Containing Proteins and Systems. QRB Discovery.
            2020;1:e10. doi:10.1017/qrd.2020.14
          </p>
          <p class='reference'>
            [7] Medhekar B, Miller JF. Diversity-generating retroelements. Curr Opin
            Microbiol. 2007;10(4):388-395. doi:10.1016/j.mib.2007.06.004.
          </p>
        "
        />
      </div>
      <Footer />
    </div>
  );
}
